import { Validators } from '@angular/forms';
import { Cast, Ignore, Model } from '@klickdata/core/application';
import {
    AfterModelInit,
    CastType,
    IDataModel,
    InitialValue,
    MethodType,
    ModelSync,
    Nullable,
} from '@klickdata/core/application/src/model/model-interface';
import { CustomField, CustomFieldData } from '@klickdata/core/custom-field';
import { Customer } from '@klickdata/core/customer';
import { Prop } from '@klickdata/core/form';
import { GradeSystem } from '@klickdata/core/grade-system';
import { Media } from '@klickdata/core/media/src/media.model';
import { ResourceOpportunity } from '@klickdata/core/opportunity';
import { Evaluation } from '@klickdata/core/opportunity/src/resource-opportunity.model';
import { Question } from '@klickdata/core/question';
import { ResourceItem, ResourceItemData } from '@klickdata/core/resource-item';
import { Task } from '@klickdata/core/task';
import { User, UserData } from '@klickdata/core/user';
import { UserNotes } from '@klickdata/core/user-notes';
import { Occasion } from '@klickdata/core/user/src/user-occasion/user-occasion.model';
import { RoleValueType } from '@klickdata/core/user/src/user.model';
import { PrivilegeType, Utils } from '@klickdata/core/util';
import { VTQData } from '@klickdata/core/vtq';
import { GeoLocation } from '@klickdata/shared-components/src/google-geolocation/geo-location-control/geo-location-control.component';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { ResourceCategory } from './category/resource-category.model';
import { Downloads } from './download.model';
import { ResourceTag, ResourceTagData } from './tag/resource-tag.model';
import { AppScope } from './type.scope.enum';
import { ResourceType } from './type/resource-type.model';
import { ResourceStaffRoles, ResourceTypes } from './types.enum';
import { DropdownOption } from '@klickdata/shared-components/src/unified-style-dropdown/unified-style-dropdown.component';

export interface ResourceData extends IDataModel {
    id?: number;
    title?: string;
    tags_attached?: boolean;
    categories_attached?: boolean;
    items_attached?: boolean;
    users_attached?: boolean;
    groups_attached?: boolean;
    folders_attached?: boolean;
    sections_attached?: boolean;
    description?: string;
    category_ids?: number[];
    tag_ids?: number[];
    customer_id?: number;
    author_id?: number;
    author_name?: string;
    tutor_name?: string;
    attested_by?: number;
    published?: string;
    occasionStatus?: string;
    last_publish?: string;
    public?: boolean;
    cluster?: boolean;
    always_available?: boolean;
    type_id?: number;
    available_types?: string;
    occasions_started?: number;
    occasions_count?: number;
    occasions_done?: number;
    start_item?: number;
    last_item?: number;
    number_of_items?: number;
    number_of_tags?: number;
    number_of_categories?: number;
    randomize_items?: boolean;
    items_per_section?: number;
    maximum_retries?: number;
    default_alternatives_count?: number;
    time_limit?: number | string;
    access_answer_value?: string;
    navigation_value?: string;
    visible_component_value?: string;
    stop_watch?: boolean;
    menu_id?: number;
    resource_item_ids?: number[];
    item_ids?: number[];
    media_id?: number;
    media_url?: string;
    article_code?: string;
    location?: GeoLocation;
    start_date?: string;
    time_zone?: string;
    started_at?: string;
    opportunity_end_date?: string;
    opportunity_started_at?: string;
    opportunity_deleted_at?: string;
    last_action?: string;
    opportunity_start_date?: string;
    opportunity_done?: string;
    end_date?: string;
    created_at?: string;
    deleted_at?: string;
    updated_at?: string;
    viewed_at?: string;
    grade_system_id?: number;
    ready_publish?: boolean;
    duration?: number | string;
    bullets?: string;
    goal?: string;
    copyrights?: string;
    instructions?: string;
    price?: number;
    promo_code?: string;
    play_time?: string;
    availability?: string;
    assigned_by?: UserSign[] | UserSign;
    assign_id?: number;
    recommended_by?: UserSign[];
    examination?: Examination;
    medias?: Medias;
    staff?: Staff;
    downloads?: Downloads;
    total_seats?: number;
    extra_seats?: number;
    included_in_courses?: number[];
    host?: number[];
    organizer?: string;
    educators?: Educator[];
    subtitles?: { [key: string]: string };
    ean_code?: string;
    other_info?: string;
    agenda?: string;
    audience?: string;
    pre_skills?: string;
    episode?: string;
    practice_document?: string;
    external_document?: string;
    video_document?: string;
    show_publisher_logo?: boolean;
    currency?: string;
    // Instructor refactor needed
    instructor?: Instructor;
    users_access_open?: boolean;
    mandatory?: boolean;
    submit?: boolean;
    priority?: number;
    user?: User;
    resource_items?: ResourceItemData[];
    occasion_dates?: OccasionDate[];
    customerOccasionDates?: OccasionDate[];
    active_occasion_dates?: OccasionDate[];
    users_count?: number;
    total_users_count?: number;
    customers_count?: number;
    started_users?: number;
    completed_users?: number;
    excluded_from_section?: boolean;
    prompt?: string;
    prompt_uuid?: string;
    prompt_error?: string;
    prompt_states?: any[];
    scorm_error?: string;

    // Available when filtered on started.
    opportunity_id?: number;

    // Language Support:
    language_id?: number;

    // statistics
    resource_started?: number;
    resource_completed?: number;
    resource_passed?: number;
    resource_failed?: number;
    total_activities?: number;

    // User Feed
    favorite?: boolean;
    like?: boolean;
    rate?: number;
    review?: string;

    /*
     * assign items
     */

    // users
    users_attach?: number[];
    users_detach?: number[];
    sync_all_users?: boolean;

    // groups
    groups_attach?: number[];
    groups_detach?: number[];
    sync_all_groups?: boolean;

    // tags
    tags_attach?: number[];
    tags_detach?: number[];

    // categories
    categories_attach?: number[];
    categories_detach?: number[];

    // customers
    customers_attach?: number[];
    customers_detach?: number[];

    // folders
    folders_attach?: number[];
    folders_detach?: number[];

    // sections
    sections_attach?: number[];
    sections_detach?: number[];
    priority_in_section?: number;

    detach_all?: ('users' | 'groups' | 'sections' | 'folders')[];
    scope_id?: AppScope;

    image?: string;
    tags?: ResourceTagData[];
    author?: UserData;
    editors?: UserData[];
    academy?: { id?: number; name?: string };
    categories?: ResourceCategory[];
    items?: ResourceItemData[];
    singleDynamicName$?: Observable<string>;
    pluralDynamicName$?: Observable<string>;

    // test or survey
    // Finish Test with submitting/answering all questions > default: False
    force_complete_sections?: boolean;
    // Take test or survey questions in order > default: false
    auto_play?: boolean;
    publish?: boolean;

    section_id?: number;
    status?: boolean;
    collaboration?: Collaboration;
    communication?: { [key: string]: any };
    address?: { [key: string]: any };
    user_collaboration?: Collaboration;
    finish_msg?: any;
    resource_notes?: UserNotes[];
    approved?: boolean;
    ai_metadata?: VTQData;
    user_permissions?: AccessControlPermission[];
    group_permissions?: AccessControlPermission[];
    team_permissions?: AccessControlPermission[];
    short_name?: string;
    done_at?: string;
    sorting_code?: number;
    recurring?: CompetenceRecurring | any;
    occasion_id?: number;
    occasion_status?: string;
    competence_expiry?: string;
    competence_status?: OccasionStatus;
    passing_levels?: { [key: string]: any }[];
    competence_label?: OccasionLabel;
    scorm_uri?: string;
    scorm?: any;
    metadata?: { [key: string]: any };
    labelTranslation?: { [key: string]: any };
    ready_play?: boolean;
    custom_fields?: CustomField[];
    duplicable?: boolean;
}

export class Resource extends Model<ResourceData> implements AfterModelInit {
    @Prop()
    public id: number;

    @Prop({
        validation: Validators.required,
    })
    public title: string;
    @Prop({
        // validation: Validators.required, // Make desciption optional.
    })
    public description: string;
    @Prop({
        validation: Validators.required,
    })
    public category_ids: number[];
    @InitialValue([])
    @Prop()
    public tag_ids: number[];
    @InitialValue([])
    public customer_id: number;
    public author_id: number;
    @Prop()
    public author_name: string;
    @Prop()
    public tutor_name: string;
    public attested_by: number;
    public published: string;
    public occasionStatus: string;
    resource_notes: UserNotes[];
    @Nullable(MethodType.PUT)
    public last_publish: string;
    @Prop()
    public public: boolean;
    @Prop()
    public cluster: boolean;
    @Prop()
    public users_access_open: boolean;
    @Prop()
    public mandatory: boolean;

    @Prop()
    public always_available: boolean;

    @Prop({
        validation: Validators.required,
    })
    @Ignore(MethodType.PUT)
    public type_id: number;

    @Ignore()
    public publish: boolean;

    @Prop({
        validation: Validators.required,
    })
    @Prop()
    public language_id: number;

    @Prop()
    public priority: number;
    public available_types: string;
    public start_item: number;
    public last_item: number;
    public recommended_by: UserSign[];
    public randomize_items: boolean;
    public items_per_section: number;
    public force_complete_sections: boolean;
    public maximum_retries: number;
    public default_alternatives_count: number;
    @Prop()
    public time_limit: number | string;
    public access_answer_value: string;
    public navigation_value: string;
    public visible_component_value: string;
    public stop_watch: boolean;
    public menu_id: number;
    @Prop()
    @Nullable(MethodType.PUT)
    @Cast(CastType.PRIMITIVE, 'number', true)
    public media_id: number;
    @Prop()
    public article_code: string;
    @Prop()
    public time_zone: string;
    public grade_system_id: number;
    public user: User;

    @Prop()
    @Cast(CastType.MOMENT, 'YYYY-MM-DD HH:mm')
    @Nullable(MethodType.PUT)
    public start_date: moment.Moment;
    @Cast(CastType.MOMENT, 'YYYY-MM-DD')
    public started_at: moment.Moment;
    @Cast(CastType.MOMENT, 'YYYY-MM-DD')
    public opportunity_end_date: moment.Moment;
    @Cast(CastType.MOMENT, 'YYYY-MM-DD')
    public opportunity_started_at: moment.Moment;
    @Cast(CastType.MOMENT, 'YYYY-MM-DD')
    public opportunity_deleted_at: moment.Moment;
    @Prop()
    public opportunity_author?: UserData;
    @Cast(CastType.MOMENT, 'YYYY-MM-DD HH:mm')
    public last_action: moment.Moment;
    @Cast(CastType.MOMENT, 'YYYY-MM-DD')
    public opportunity_start_date: moment.Moment;
    @Cast(CastType.OBJECT)
    public metadata: { [key: string]: any };
    @Cast(CastType.MOMENT, 'YYYY-MM-DD HH:mm')
    @Nullable(MethodType.PUT)
    public end_date: moment.Moment;
    @Cast(CastType.MOMENT)
    public viewed_at: moment.Moment;
    public opportunity_done: string;
    @InitialValue([])
    public resource_item_ids: number[];
    @InitialValue([])
    public item_ids: number[];

    // Available when filtered on started.
    public opportunity_id: number;

    // User Feed
    @Ignore()
    @InitialValue({})
    public feeds: Feeds;
    // User Feed
    @Ignore()
    @InitialValue({})
    public user_feeds: Feeds;

    /**
     * Assign/de-assign resource with items
     */

    // users
    @InitialValue([])
    users_attach: number[];
    @InitialValue([])
    users_detach: number[];
    sync_all_users: boolean;

    // groups
    @InitialValue([])
    groups_attach: number[];
    @InitialValue([])
    groups_detach: number[];
    sync_all_groups: boolean;

    /**
     * Ignore from adding to payload.
     */
    @Ignore()
    checked: boolean;
    @Ignore()
    occasions_started: number;
    @Ignore()
    occasions_count: number;
    @Ignore()
    occasions_done: number;
    @Ignore()
    @Cast(CastType.MOMENT)
    public created_at: moment.Moment;
    @Cast(CastType.MOMENT)
    @Ignore()
    public deleted_at: moment.Moment;
    @Ignore()
    public author: any;

    @Cast(CastType.MOMENT)
    @Ignore()
    public updated_at: moment.Moment;

    @Ignore()
    public tags_attached: boolean;
    @Ignore()
    public items_attached: boolean;
    @Ignore()
    public categories_attached: boolean;
    @Ignore()
    public categories: ResourceCategory[];
    @Ignore()
    public users_attached: boolean;
    @Ignore()
    public groups_attached: boolean;
    @Ignore()
    public folders_attached: boolean;
    @Ignore()
    public sections_attached: boolean;
    @Ignore()
    public auto_play: boolean;
    @Ignore()
    public purchased: boolean;
    @Ignore()
    public number_of_items: number;
    @Ignore()
    public number_of_tags: number;
    @Ignore()
    public number_of_categories: number;
    @Ignore()
    public ready_publish: boolean;
    @Ignore()
    public users_count: number;
    @Ignore()
    public total_users_count: number;
    @Ignore()
    public customers_count: number;
    @Ignore()
    public started_users: number;
    @Ignore()
    public completed_users: number;
    @Ignore()
    public excluded_from_section: boolean;
    @Ignore()
    @InitialValue(0)
    public priority_in_section: number;
    // handle selection
    @Ignore()
    public selected: boolean;
    // Handle loading.
    @Ignore()
    public loading: boolean;
    @Ignore()
    resource_started = 0;
    @Ignore()
    resource_completed = 0;
    @Ignore()
    resource_passed = 0;
    @Ignore()
    resource_failed = 0;
    @Ignore()
    total_activities = 0;
    @Ignore()
    public resourceType: ResourceType;
    @Prop()
    public resource_items?: ResourceItemData[];
    @Ignore()
    public submit: boolean;
    @Ignore()
    public type_value: string;
    @Prop()
    public duration: number | string;
    @Prop({
        validation: Validators.required,
    })
    public bullets?: string;
    @Prop()
    public goal?: string;
    @Prop()
    public copyrights?: string;
    @Prop()
    public instructions?: string;
    @Prop()
    @Nullable()
    public price?: number;
    @Prop()
    public promo_code?: string;
    @Prop()
    public play_time?: string;
    public availability?: string;
    @Prop()
    public assigned_by?: UserSign[] | UserSign;
    @Prop()
    public assign_id?: number;
    @Cast(CastType.OBJECT)
    @Prop()
    public examination?: Examination;
    @Nullable()
    @Prop()
    public passing_levels?: { [key: string]: any }[];
    @Cast(CastType.CLOSURE, Utils.nullableMediaSync)
    @Prop()
    public medias?: Medias;
    @Cast(CastType.OBJECT)
    @Prop()
    public staff?: Staff;
    @Cast(CastType.OBJECT)
    @Prop()
    public location?: GeoLocation;
    @Prop()
    public total_seats?: number | 'unlimited';
    @Prop()
    public host?: number[];
    @Prop()
    public organizer?: string;
    @Prop()
    public extra_seats?: number;
    @Prop()
    public included_in_courses?: number[];
    @Prop()
    @Cast(CastType.OBJECT)
    subtitles: { [key: string]: string };
    @Prop()
    public ean_code?: string;
    @Prop()
    public other_info?: string;
    @Prop()
    agenda?: string;
    @Prop()
    audience?: string;
    @Prop()
    pre_skills?: string;
    @Prop()
    episode?: string;
    @Prop()
    practice_document?: string;
    @Prop()
    external_document?: string;
    @Prop()
    video_document?: string;
    @Prop()
    public verified?: boolean;
    @Prop()
    public show_publisher_logo?: boolean;
    @Prop()
    public duplicable?: boolean;
    @Prop()
    @Cast(CastType.CLOSURE, (items: OccasionDate[]) =>
        items.map((item) => {
            // Remove 'selected' property
            const { selected, ...rest } = item;
            // Remove application_start_date and application_end_date if null or undefined
            const cleaned: OccasionDate = { ...rest };
            if (cleaned.application_start_date == null) {
                delete cleaned.application_start_date;
            }
            if (cleaned.application_end_date == null) {
                delete cleaned.application_end_date;
            }
            // If both start_date and end_date are missing, set application_start_date and application_end_date to null
            if (!cleaned.start_date && !cleaned.end_date) {
                delete cleaned.application_start_date;
                delete cleaned.application_end_date;
            }
            if (!cleaned.id) {
                delete cleaned.id;
            }
            return cleaned;
        })
    )
    public occasion_dates?: OccasionDate[];
    @Ignore()
    public enrollment_counts?: any;
    @Ignore()
    public customerOccasionDates?: OccasionDate[];
    @Ignore()
    public active_occasion_dates?: OccasionDate[];

    @Prop()
    @InitialValue('USD')
    public currency?: string;

    // @Nullable(MethodType.PUT)
    // @Cast(CastType.OBJECT)
    // @Prop()
    public instructor: Instructor;
    @Ignore()
    public occasions: Occasion[];
    @Ignore()
    public assignStatus: string;
    @Ignore()
    @Cast(CastType.MOMENT)
    public assign_deleted_at: string;
    @Ignore()
    public downloads?: Downloads;
    @Ignore()
    public exercise_downloads?: Downloads;
    public scope_id: AppScope;
    @Ignore()
    public items: ResourceItem[];
    @Ignore()
    public quote_ids: number[];
    @Ignore()
    public related_resources: ResourceData[];
    @Ignore()
    public done: string;
    @Cast(CastType.CLOSURE, Utils.modelSync)
    users?: ModelSync;
    @Cast(CastType.CLOSURE, Utils.modelSync)
    groups?: ModelSync;
    @Cast(CastType.CLOSURE, Utils.modelSync)
    teams?: ModelSync;
    public educators: Educator[];
    public hosts: Educator[];
    public organizers: Educator[];
    public publishers: Educator[];
    public managers: Educator[];
    /**
     * Observables will be ignore when generate payload on base module.
     * And you still can use @see @Ignore()
     */
    public questions: Observable<Question[]>;
    public type: Observable<ResourceType>;
    public items$: Observable<ResourceItem[]>;
    public item$: Observable<ResourceItem>;
    public categories$: Observable<ResourceCategory[]>;
    public tags$: Observable<ResourceTag[]>;
    public gradeSystem: Observable<GradeSystem>;
    public author$: Observable<User>;
    public managers$: Observable<Educator[]>;
    public educators$: Observable<Educator[]>;
    public publishers$: Observable<Educator[]>;
    public hosts$: Observable<Educator[]>;
    public organizers$: Observable<Educator[]>;
    public customer: Observable<Customer>;
    public opportunity: Observable<ResourceOpportunity>;
    public media$: Observable<Media>;
    @Ignore()
    public media_url: string;
    @Ignore()
    public video_url: string;
    public media_fallback_url: string;
    @Ignore()
    public opportunity_assign_date: string;
    @Ignore()
    public label: string;
    // @Cast(CastType.OBJECT)
    @Prop()
    public collaboration: Collaboration;
    @Cast(CastType.OBJECT)
    @Prop()
    public communication: { [key: string]: any };
    @Cast(CastType.OBJECT)
    @Prop()
    public address: { [key: string]: any };
    @Prop()
    public finish_msg: any;

    // Evaluation needed keys
    approved?: boolean;
    occasion_id?: number;
    evaluation?: Evaluation;
    @Ignore()
    enrollment: ResourceApproval;
    @Ignore()
    user_enrollment: ResourceApproval;
    @Ignore()
    publication: ResourceApproval;
    @Ignore()
    publishment: ResourceApproval;
    @Ignore()
    enrollments: ResourceApproval[];
    @Ignore()
    user_collaboration: Collaboration | any;
    @Ignore()
    enroll_approval_status: null | 'required' | 'pending' | 'approved' | 'rejected';
    @Ignore()
    available_seats: number;
    user_permissions?: AccessControlPermission[];
    group_permissions?: AccessControlPermission[];
    team_permissions?: AccessControlPermission[];
    @Ignore()
    short_name: string;
    @Cast(CastType.MOMENT)
    @Ignore()
    public done_at: moment.Moment;
    @Ignore()
    public occasion_status: string;
    @Ignore()
    public competence_expiry: string;
    @Prop()
    public sorting_code: number;
    @Ignore()
    all_resource_perm_checked: boolean;
    @Ignore()
    some_resource_perm_checked: boolean;
    @Nullable()
    @Prop()
    public scorm_uri: string;
    @Ignore()
    public scorm: any;
    @Ignore()
    public prompt: string;
    @Ignore()
    public prompt_uuid: string;
    @Ignore()
    public prompt_error: string;
    @Ignore()
    public prompt_states: any[];
    @Ignore()
    public user_competence: any;

    public isPublished(): boolean {
        return !!(this.published && this.last_publish);
    }

    @Ignore()
    recurring?: CompetenceRecurring;
    @Ignore()
    hasAvailableSeats?: boolean;
    @Ignore()
    tags?: ResourceTagData[];
    @Ignore()
    enrollmentRequiredMetadata?: any;
    @Ignore()
    enrollmentMetadata?: any;
    @Ignore()
    enrollmentRequiredStatus?: boolean;
    @Ignore()
    playbackAction?: 'start' | 'repeat' | 'continue';
    @Ignore()
    editors?: any;
    @Ignore()
    academy?: { id?: number; name?: string };
    @Ignore()
    accessRole?: string;
    @Ignore()
    public labelTranslation: { [key: string]: any };
    @Ignore()
    public scorm_error: string;
    @Ignore()
    public ready_play: boolean;
    @Ignore()
    singleDynamicName$: Observable<string>;
    @Ignore()
    pluralDynamicName$: Observable<string>;
    @Ignore()
    playableResourceCheck$: Observable<PlayableResourceSpec>;
    @Ignore()
    playableResource$: Observable<PlayableResourceSpec>;
    @Cast(CastType.CLOSURE, (value: CustomFieldData[], old: CustomFieldData[]) => {
        const oldFields = new Map(old?.map((f) => [f.id, f.value]));
        const updates = value
            .filter((field) => field.value !== oldFields?.get(field.id))
            .map((f) => ({ id: f.id, value: f.value }));

        return updates.length ? updates : undefined;
    })
    custom_fields: CustomField[];
    public isAvailable(): boolean {
        if (this.always_available) {
            return true;
        }

        if (!this.start_date && !this.end_date) {
            return true;
        }

        if (this.start_date && this.end_date) {
            return this.start_date.isBefore() && this.end_date.isAfter();
        }

        if (this.start_date && !this.end_date) {
            return this.start_date.isBefore();
        }

        if (!this.start_date && this.end_date) {
            return this.end_date.isAfter();
        }

        return false;
    }
    get finalOccasion(): Occasion {
        return Array.isArray(this.occasions) ? this.occasions[0] : this.occasions;
    }

    get assignedByName() {
        return Array.isArray(this.assigned_by) ? this.assigned_by[0]?.name : this.assigned_by?.name;
    }

    get favorite(): boolean {
        return this.user_feeds?.favorite;
    }

    set favorite(favorite: boolean) {
        this.user_feeds = Utils.isEmpty(this.user_feeds) ? { favorite: favorite } : { ...this.user_feeds, favorite };
    }

    get like(): boolean {
        return this.user_feeds?.like;
    }

    set like(like: boolean) {
        this.user_feeds = Utils.isEmpty(this.user_feeds) ? { like: like } : { ...this.user_feeds, like };
    }

    get rate(): number {
        return this.user_feeds?.rate;
    }
    set rate(rate: number) {
        this.user_feeds = Utils.isEmpty(this.user_feeds) ? { rate: rate } : { ...this.user_feeds, rate };
    }

    get review(): string {
        return this.user_feeds?.review;
    }
    set review(review: string) {
        this.user_feeds = Utils.isEmpty(this.user_feeds) ? { review: review } : { ...this.user_feeds, review };
    }

    afterModelInit(data: ResourceData): void {
        if (!data.currency) {
            data.currency = 'USD';
        }

        if (data.recurring?.recurring && !data.recurring.notify_expiry) {
            data.recurring.notify_expiry = Utils.getExpiryNotifyDays(parseInt(data.recurring.recurring));
        }
    }
}

export interface ResourceWithQuery {
    resource: Resource;
    params: {};
}

export interface CoursePlanResource {
    resource: Resource;
    count: number;
    doneCount: number;
    opportunities: ResourceOpportunity[];
}
export interface ApprovalMessage {
    subject?: string;
    body?: string;
    author_id?: number;
    parent_id?: number;
}
export interface ResourceApproval {
    requested_at?: string;
    reviewed_at?: string;
    updated_at?: string;
    approval_status?: 'pending' | 'approved' | 'rejected' | 'waiting' | 'enrolled';
    applicant?: UserData;
    admin?: UserData;
    collaboration_id?: number;
    metadata?: any;
    requested_by_me?: boolean;
}
export interface Collaboration {
    view?: any;
    mandatory?: boolean;
    enroll_approval_required?: any;
    signoff_required?: boolean;
    edit?: boolean;
    publish?: boolean;
    tutor?: boolean;
}
export interface StatusCollaboration {
    view: StatusCollaborationItem;
    mandatory: StatusCollaborationItem;
    enroll_approval_required: StatusCollaborationItem;
    signoff_required: StatusCollaborationItem;
}
export interface StatusCollaborationItem {
    status: boolean;
    exclude: boolean;
    metadata?: any;
}
export interface AccessControlPermission {
    id?: number;
    permission_type?: string;
    permission_value?: boolean;
    disabled?: boolean;
}

export interface Educator {
    id?: number;
    name: string;
    about?: string;
    fname?: string;
    lname?: string;
    title?: string;
    phone?: string;
    email?: string;
    role_value?: RoleValueType;
    media_id?: number;
    media?: Media;
    media$?: Observable<Media>;
    customer_id?: number;
    customer?: Customer;
    resource_id?: number;
    role?: ResourceStaffRoles;
    task?: Task;
    communications?: {};
}
export interface Instructor {
    name: string;
    images: number[];
}

export interface UserSign {
    name: string;
    id: number;
}

export interface Examination {
    type: 'Participation' | 'Diploma' | 'Certification';
    label?: string;
    grade_system_id: number;
    final_test_id: number;
    diploma_level: number;
}
export interface TestPassingPercentage {
    user_percentage?: number;
    percentage?: number;
    repeats?: number;
    user_repeats?: number;
}
export interface Medias {
    cover?: number[];
    background?: number[];
    screenshot?: number[];
    publisher?: number[];
    trailer?: number[];
    presentation?: number[];
    brochure?: number[];
    exercise?: number[];
    logotype?: number[];
    file?: number[];
    avatar?: number[];
    footerLogotype?: number[];
    boardingLogo?: number[];
    diploma?: { id: number; description: string }[];
}
export interface Staff {
    educator?: number[];
    manager?: number[];
    publisher?: number[];
    host?: number[];
    organizer?: number[];
    creator?: number[];
    quotee?: number[];
    assignee?: number[];
    contact?: number[];
}

export interface Feeds {
    favorite?: boolean;
    like?: boolean;
    rate?: number;
    review?: string;
}
export interface OccasionDate {
    ispassed?: boolean;
    start_date: string;
    end_date: string;
    id?: number;
    isValid?: boolean;
    hasAvailableSeats?: boolean;
    hasValidDate?: boolean;
    selected?: boolean;
    disabled?: boolean;
    show_application_period?: boolean;
    application_start_date?: string;
    application_end_date?: string;
    total_seats?: number;
    approved_seats?: number;
    approved_count?: number;
    enrollment_counts?: any;
    opportunity_id?: number;
    occasionStatus?: string;
    location?: string;
    grib?: string;
    activity?: string;
    other?: string;
    target_group?: string;
    expanded?: boolean;
}
export interface ResourceEventType {
    id: number;
    type: string;
}
export interface Currency {
    code: string;
    sign: string;
    label: string;
}

export interface CompetenceRecurring {
    recurring?: string;
    recurring_mode?: 'completion' | 'period_start' | null;
    start_date?: string;
    sorting_code?: string;
    notify_expiry?: string;
    competence_expiry?: string;
    done_at?: string;
}
export interface PlayableResourceSpec {
    /** Whether the user can proceed (play/enroll/continue). When false, use checkType to determine the blocking reason (e.g. PRICE for payment required). */
    isAllowed?: boolean;
    /** Whether the button/action is clickable. */
    clickable?: boolean;
    label?: string;
    icon?: string;
    checkType?: ResourceCheckKeys;
    desc?: string;
    fg?: string;
    bg?: string;
}
export interface BypassResourceCheckKey {
    application_date: boolean;
    occasion_date: boolean;
    seats: boolean;
    enroll: boolean;
    price: boolean;
    items: boolean;
    other: boolean;
    opportunity: boolean;
    mandatory: boolean;
}
export interface AiModelSpecs {
    name: string;
    value: string;
    enabled?: boolean;
    default?: boolean;
    divider?: boolean;
    options?: DropdownOption[];
}
export enum ResourceCheckKeys {
    APPLICATION_DATE = 'application_date',
    OCCASION_DATE = 'occasion_date',
    SEATS = 'seats',
    ENROLL = 'enroll',
    PRICE = 'price',
    ITEMS = 'items',
    OTHER = 'other',
    OPPORTUNITY = 'opportunity',
    MANDATORY = 'mandatory',
}
export enum MaterialModelType {
    TEXT = 'text',
    IMAGE = 'image',
    VIDEO = 'video',
    AUDIO = 'audio',
    TRAILER = 'trailer',
    TRAILER_VIDEO = 'video/trailer',
    PRESENTATION_VIDEO = 'video/presentation',
}
export type OccasionStatus = 'red' | 'green' | 'orange' | 'iceblue' | 'whiteonred' | 'grey';
export type OccasionLabel =
    | 'H1'
    | 'H2' // Half-yearly
    | 'T1'
    | 'T2'
    | 'T3' // Tertial
    | 'Q1'
    | 'Q2'
    | 'Q3'
    | 'Q4' // Quarterly
    | `W${number}` // Weekly (e.g., W1, W52)
    | `D${number}` // Daily (e.g., D1, D365)
    | string; // To cover Monthly (e.g., 'Ja', 'Fe') and Yearly (e.g., '23' for 2023)
